import React, { useContext } from "react";
import styled from "@emotion/styled";
import { hot } from "react-hot-loader/root";
import { sum, range } from "d3-array";
import { useTheme } from "emotion-theming";

import * as S from "./Explainer.styles.js";
import { is768Query, is1200Query } from "~/core/scripts/mediaquery-helper";
import { TranslationContext } from "~/kits/general/scripts/app.js";

import Icon from "~/core/scripts/components/Icon";
import Board from "~/components/Board";
import Elector from "~/components/Elector";
import ResultBoard from "~/components/ResultBoard";
import ElectionGrid from "~/components/Board/layers/ElectionGrid";
import DistrictsOverlay from "~/components/Board/layers/DistrictsOverlay";

const CenterOnDesktop = styled.div`
  ${(p) => p.theme.breakpoints.sm.up} {
    text-align: center;
  }
`;

const Explainer = () => {
  const theme = useTheme();
  const polyglot = useContext(TranslationContext);
  const isDesktop = is768Query();
  const isLargeDesktop = is1200Query();
  const houses = [...range(16).map(() => true), ...range(11).map(() => false)];
  const electors = [...range(3).map(() => true), ...range(6).map(() => false)];
  const hideIfEmpty = (str) => polyglot.t(str) !== str;

  return (
    <S.Wrapper>
      <CenterOnDesktop>
        <div className="text-wrap">
          <h3
            dangerouslySetInnerHTML={{ __html: polyglot.t("explainerTitle") }}
          />
          {hideIfEmpty("explainerIntro") && (
            <S.Sans
              dangerouslySetInnerHTML={{ __html: polyglot.t("explainerIntro") }}
            />
          )}
        </div>
      </CenterOnDesktop>

      <S.InnerWrapper>
        <S.StickyWrapper>
          <S.BoardTitle>{polyglot.t("state1")}</S.BoardTitle>
          <Board>
            <ElectionGrid
              small={!isLargeDesktop}
              matrix={[
                [true, true, true],
                [true, true, true],
                [true, true, false],
              ]}
            />
            {false && (
              <DistrictsOverlay fill={true} matrix={[true]} isBiden={true} />
            )}
          </Board>
        </S.StickyWrapper>
        <S.StoryItems>
          <S.StoryItemWrapper isActive>
            <S.CenterOnMobile>
              {hideIfEmpty("state1above") && (
                <p
                  dangerouslySetInnerHTML={{
                    __html: polyglot.t("state1above"),
                  }}
                />
              )}
              <ResultBoard
                background="#FFFFFF"
                trumpOverallVotes={1}
                bidenOverallVotes={8}
                trumpWon={false}
                bidenWon={true}
              />
              {hideIfEmpty("state1middle") && (
                <p
                  dangerouslySetInnerHTML={{
                    __html: polyglot.t("state1middle"),
                  }}
                />
              )}
              {/* {range(3).map((i) => (
                <Elector
                  key={`elector-${i}`}
                  color={theme.colors.democratic}
                  number={i + 1}
                />
              ))} */}
              {hideIfEmpty("state1end") && (
                <p
                  dangerouslySetInnerHTML={{ __html: polyglot.t("state1end") }}
                />
              )}
            </S.CenterOnMobile>
          </S.StoryItemWrapper>
        </S.StoryItems>
      </S.InnerWrapper>

      <S.InnerWrapper>
        <S.StickyWrapper>
          <S.BoardTitle>{polyglot.t("state2")}</S.BoardTitle>
          <Board>
            <ElectionGrid
              small={!isLargeDesktop}
              matrix={[
                [true, true, false],
                [true, false, false],
                [true, false, false],
              ]}
            />
            {false && (
              <DistrictsOverlay fill={true} matrix={[false]} isBiden={false} />
            )}
          </Board>
        </S.StickyWrapper>
        <S.StoryItems>
          <S.StoryItemWrapper isActive>
            <S.CenterOnMobile>
              {hideIfEmpty("state2above") && (
                <p
                  dangerouslySetInnerHTML={{
                    __html: polyglot.t("state2above"),
                  }}
                />
              )}
              <ResultBoard
                background="#FFFFFF"
                trumpOverallVotes={5}
                bidenOverallVotes={4}
                trumpWon={true}
                bidenWon={false}
              />
              {hideIfEmpty("state2middle") && (
                <p
                  dangerouslySetInnerHTML={{
                    __html: polyglot.t("state2middle"),
                  }}
                />
              )}
              {/* {range(3).map((i) => (
                <Elector
                  key={`elector-${i}`}
                  color={theme.colors.republican}
                  number={i + 1}
                />
              ))} */}
              {hideIfEmpty("state2end") && (
                <p
                  dangerouslySetInnerHTML={{ __html: polyglot.t("state2end") }}
                />
              )}
            </S.CenterOnMobile>
          </S.StoryItemWrapper>
        </S.StoryItems>
      </S.InnerWrapper>

      <S.InnerWrapper>
        <S.StickyWrapper>
          <S.BoardTitle>{polyglot.t("state3")}</S.BoardTitle>
          <Board>
            <ElectionGrid
              small={!isLargeDesktop}
              matrix={[
                [true, true, false],
                [true, false, false],
                [true, false, false],
              ]}
            />
            {false && (
              <DistrictsOverlay fill={true} matrix={[false]} isBiden={false} />
            )}
          </Board>
        </S.StickyWrapper>
        <S.StoryItems>
          <S.StoryItemWrapper isActive>
            <S.CenterOnMobile>
              {hideIfEmpty("state3above") && (
                <p
                  dangerouslySetInnerHTML={{
                    __html: polyglot.t("state3above"),
                  }}
                />
              )}
              <ResultBoard
                background="#FFFFFF"
                trumpOverallVotes={5}
                bidenOverallVotes={4}
                trumpWon={true}
                bidenWon={false}
              />
              {hideIfEmpty("state3middle") && (
                <p
                  dangerouslySetInnerHTML={{
                    __html: polyglot.t("state3middle"),
                  }}
                />
              )}
              {/* {range(3).map((i) => (
                <Elector
                  key={`elector-${i}`}
                  color={theme.colors.republican}
                  number={i + 1}
                />
              ))} */}
              {hideIfEmpty("state3end") && (
                <p
                  dangerouslySetInnerHTML={{ __html: polyglot.t("state3end") }}
                />
              )}
            </S.CenterOnMobile>
          </S.StoryItemWrapper>
        </S.StoryItems>
      </S.InnerWrapper>

      <S.InnerWrapper>
        {hideIfEmpty("resultsAbove") && !isDesktop && (
          <S.StoryItems>
            <S.StoryItemWrapper isActive>
              <p
                dangerouslySetInnerHTML={{ __html: polyglot.t("resultsAbove") }}
              />
            </S.StoryItemWrapper>
          </S.StoryItems>
        )}
        <S.StickyWrapper>
          <S.ElectorsTitle isBiden>
            {sum(houses)} {polyglot.t("popularVote")}
          </S.ElectorsTitle>
          <S.HousesContainer>
            {houses
              .filter((isBiden) => isBiden)
              .map((isBiden, i, a) => (
                <S.HouseContainer key={`house-${i}`}>
                  <Icon
                    style={{ fontSize: 25 }}
                    type={isBiden ? "house--biden" : "house--trump"}
                  />
                </S.HouseContainer>
              ))}
          </S.HousesContainer>
          <S.ElectorsTitle isBiden={false}>
            {houses.length - sum(houses)} {polyglot.t("popularVote")}
          </S.ElectorsTitle>
          <S.HousesContainer>
            {houses
              .filter((isBiden) => !isBiden)
              .map((isBiden, i, a) => (
                <S.HouseContainer key={`house-${i}`}>
                  <Icon
                    style={{ fontSize: 25 }}
                    type={isBiden ? "house--biden" : "house--trump"}
                  />
                </S.HouseContainer>
              ))}
          </S.HousesContainer>
        </S.StickyWrapper>

        <S.StoryItems>
          <S.StoryItemWrapper isActive>
            {hideIfEmpty("resultsAbove") && isDesktop && (
              <p
                dangerouslySetInnerHTML={{ __html: polyglot.t("resultsAbove") }}
              />
            )}
            {hideIfEmpty("resultsMiddle") && (
              <p
                dangerouslySetInnerHTML={{
                  __html: polyglot.t("resultsMiddle"),
                }}
              />
            )}
          </S.StoryItemWrapper>
        </S.StoryItems>
        <S.StickyWrapper>
          <S.ElectorsTitle>1. Bundesstaat</S.ElectorsTitle>
          <S.ElectorsContainer>
            {range(3).map((i) => (
              <Elector
                key={`elector-${i}`}
                color={theme.colors.democratic}
                number={i + 1}
              />
            ))}
          </S.ElectorsContainer>
          <S.TwoColumns>
            <S.ElectorsTitle>2. Bundesstaat</S.ElectorsTitle>
            <S.ElectorsTitle>3. Bundesstaat</S.ElectorsTitle>
          </S.TwoColumns>
          <S.TwoColumns>
            <S.ElectorsContainer>
              {range(3).map((i) => (
                <Elector
                  key={`elector-${i}`}
                  color={theme.colors.republican}
                  number={i + 1}
                />
              ))}
            </S.ElectorsContainer>
            <S.ElectorsContainer>
              {range(3).map((i) => (
                <Elector
                  key={`elector-${i}`}
                  color={theme.colors.republican}
                  number={i + 1 + 3}
                />
              ))}
            </S.ElectorsContainer>
          </S.TwoColumns>
        </S.StickyWrapper>

        <S.StoryItems>
          <S.StoryItemWrapper isActive>
            {hideIfEmpty("resultsEnd") && (
              <p
                dangerouslySetInnerHTML={{ __html: polyglot.t("resultsEnd") }}
              />
            )}
          </S.StoryItemWrapper>
        </S.StoryItems>
      </S.InnerWrapper>
    </S.Wrapper>
  );
};

export default hot(Explainer);
